import React, { useState, useEffect, useMemo } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { UserPlus, Copy, CheckCircle, Shield, Store, Plus, Minus } from 'lucide-react';
import { toast } from '../../lib/safeToast';
import { getApiErrorMessage } from '../../lib/utils';

export default function BackOfficeUsersInvite() {
  const { api, user: currentUser } = useAuth();
  const [formData, setFormData] = useState({ email: '', name: '', role: 'viewer', merchant_id: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [users, setUsers] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    api.get('/backoffice/merchants-list').then((r) => setMerchants(r.data?.data || [])).catch(() => {});
    api.get('/users').then((r) => setUsers(r.data?.data || [])).catch(() => {});
  }, [api]);

  const merchantsSorted = useMemo(() => {
    const master = merchants.filter((m) => m.name === 'Master organisation');
    const rest = merchants.filter((m) => m.name !== 'Master organisation').sort((a, b) => a.name.localeCompare(b.name));
    return [...master, ...rest];
  }, [merchants]);

  const merchantsWithUsers = useMemo(() => {
    return merchantsSorted.map((m) => ({
      ...m,
      users: users.filter((u) => u.merchant_id === m.id),
    }));
  }, [merchantsSorted, users]);

  const toggleExpand = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const needsMerchant = formData.role === 'viewer' || formData.role === 'manager';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsMerchant && !formData.merchant_id) {
      toast.error('Please select an organisation for Viewer or Manager.');
      return;
    }
    setLoading(true);
    try {
      const payload = { email: formData.email, name: formData.name, role: formData.role };
      if (needsMerchant && formData.merchant_id) payload.merchant_id = formData.merchant_id;
      const res = await api.post('/users', payload);
      setResult(res.data.data);
      toast.success('User created');
      setFormData({ email: '', name: '', role: 'viewer', merchant_id: '' });
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to create user'));
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const selectedMerchantName = merchants.find((m) => m.id === formData.merchant_id)?.name || '';

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <UserPlus className="w-7 h-7 text-[#0B6445]" />
            Add / Invite User
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">Create a new platform user with a temporary password.</p>
        </div>

        {result && (
          <Card className="border-green-200 bg-green-50 shadow-sm">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-2 text-green-700 font-semibold">
                <CheckCircle className="w-5 h-5" /> User Created
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500 text-xs uppercase">Name</span><p className="font-medium">{result.name}</p></div>
                <div><span className="text-slate-500 text-xs uppercase">Email</span><p className="font-medium">{result.email}</p></div>
                <div><span className="text-slate-500 text-xs uppercase">Role</span><p className="font-medium capitalize">{result.role?.replace('_', ' ')}</p></div>
                {result.temp_password && (
                  <div>
                    <span className="text-slate-500 text-xs uppercase">Temporary Password</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-2 py-1 rounded border text-sm font-mono">{result.temp_password}</code>
                      <button onClick={() => copyText(result.temp_password, 'pwd')} className="text-[#0B6445] hover:text-[#073B2E]">
                        {copied === 'pwd' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setResult(null)} className="mt-2 border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]">
                Create Another
              </Button>
            </CardContent>
          </Card>
        )}

        {!result && (
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
                <div className="space-y-1.5">
                  <Label>Full Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Jane Smith" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="jane@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v, merchant_id: '' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merchant_admin">Merchant Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {needsMerchant && (
                  <div className="space-y-1.5">
                    <Label>Organisation</Label>
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-between text-left font-normal border-[#DCE4E0] hover:bg-[#F8FAFC] hover:text-[#073B2E]">
                          {selectedMerchantName || 'Select organisation…'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 max-h-72 overflow-y-auto p-2">
                        {merchantsWithUsers.map((m) => (
                          <div key={m.id}>
                            <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F1F5F9] cursor-pointer text-sm">
                              <button type="button" onClick={() => toggleExpand(m.id)} className="shrink-0 text-slate-400 hover:text-slate-700">
                                {expandedIds.has(m.id) ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                              </button>
                              <button type="button" onClick={() => { setFormData({ ...formData, merchant_id: m.id }); setPopoverOpen(false); }} className="flex items-center gap-1.5 truncate">
                                {m.name === 'Master organisation' ? <Shield className="w-3.5 h-3.5 text-[#0B6445]" /> : <Store className="w-3.5 h-3.5 text-slate-400" />}
                                <span className={formData.merchant_id === m.id ? 'font-semibold text-[#073B2E]' : ''}>{m.name}</span>
                              </button>
                            </div>
                            {expandedIds.has(m.id) && m.users.length > 0 && (
                              <div className="ml-8 mb-1 border-l border-slate-200 pl-2">
                                {m.users.map((u) => (
                                  <div key={u.id} className="text-xs text-slate-500 py-0.5 truncate">{u.name || u.email} — {u.role?.replace('_', ' ')}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                <Button type="submit" disabled={loading} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">
                  {loading ? 'Creating…' : 'Create User'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </BackOfficeLayout>
  );
}
